import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Lock, Pause, Play } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE, normalizeBeats, startCheckout } from "@/lib/api";
import { Reveal, SectionEyebrow } from "@/components/Reveal";

const PREVIEW_CAP = 60;
const fmt = (s) => `0:${String(Math.floor(s)).padStart(2, "0")}`;

const Waveform = ({ peaks, progress, onSeek }) => (
  <div
    className="flex items-end gap-[2px] h-10 flex-1 cursor-pointer group/wf"
    data-testid="beat-waveform"
    onClick={(e) => {
      const r = e.currentTarget.getBoundingClientRect();
      onSeek(Math.min(Math.max((e.clientX - r.left) / r.width, 0), 1));
    }}
  >
    {peaks.map((p, i) => {
      const played = i / peaks.length <= progress;
      return (
        <div
          key={i}
          className={`flex-1 transition-colors duration-150 ${
            played ? "bg-gradient-to-t from-[#D65BB4] to-[#4DBDBE]" : "bg-white/15 group-hover/wf:bg-white/30"
          }`}
          style={{ height: `${Math.round(p * 100)}%`, minHeight: "3px" }}
        />
      );
    })}
  </div>
);

const BeatRow = ({ beat, idx, tiers, audio, current, playing, time, onToggle, onSeek, leaseOpen, onLeaseOpen }) => {
  const isCurrent = current === beat.id;
  const progress = isCurrent ? Math.min(time / PREVIEW_CAP, 1) : 0;
  const leaseTiers = tiers.filter((t) => t.lookup_key !== "exclusive");
  const slug = beat.title.toLowerCase().replace(/\s+/g, "-");

  return (
    <div
      className={`border border-white/10 bg-white/[0.03] transition-colors duration-300 ${
        isCurrent ? "border-transparent outline outline-1 outline-[#D65BB4]/60 shadow-[0_0_40px_rgba(214,91,180,0.15)]" : "hover:bg-white/[0.06]"
      }`}
      data-testid={`beat-row-${slug}`}
    >
      <div className="grid grid-cols-1 md:grid-cols-[auto_minmax(0,240px)_1fr_auto] gap-x-8 gap-y-4 items-center px-5 md:px-8 py-5">
        <div className="flex items-center gap-5">
          <span className="font-display text-white/30 text-sm w-8">{String(idx + 1).padStart(2, "0")}</span>
          <button
            data-testid={`beat-play-${slug}`}
            onClick={() => onToggle(beat)}
            aria-label={isCurrent && playing ? `Pause preview of ${beat.title}` : `Play preview of ${beat.title}`}
            className={`w-12 h-12 flex items-center justify-center transition-all duration-200 ${
              isCurrent && playing
                ? "bg-[#4DBDBE] text-black hover:shadow-[0_0_28px_rgba(90,215,214,0.55)]"
                : "bg-[#D65BB4] text-white hover:shadow-[0_0_28px_rgba(214,91,180,0.55)] hover:scale-105"
            }`}
          >
            {isCurrent && playing ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
          </button>
        </div>

        <div>
          <div className="font-display font-bold uppercase text-sm md:text-base">{beat.title}</div>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-white/50 tracking-widest uppercase">
            <span className="px-2 py-0.5 border border-white/15 text-white/70">{beat.tag}</span>
            <span>{beat.bpm} BPM</span>
            <span>{beat.musical_key}</span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <Waveform peaks={beat.waveform} progress={progress} onSeek={(f) => onSeek(beat, f)} />
          <span className="text-xs text-white/50 tabular-nums w-20 text-right" data-testid={`beat-time-${slug}`}>
            {isCurrent ? `${fmt(time)} / 1:00` : "1:00 preview"}
          </span>
        </div>

        <div className="flex items-center gap-2 md:justify-end">
          <button
            data-testid={`beat-lease-${slug}`}
            onClick={() => onLeaseOpen(isCurrent && leaseOpen ? null : beat.id)}
            className="flex items-center gap-1.5 border border-white/25 px-4 py-2.5 text-xs uppercase tracking-widest font-semibold hover:border-[#4DBDBE] hover:text-[#4DBDBE] transition-colors"
          >
            Lease <ChevronDown size={13} className={`transition-transform ${leaseOpen && isCurrent ? "rotate-180" : ""}`} />
          </button>
          {beat.exclusive_available ? (
            <button
              data-testid={`beat-own-${slug}`}
              onClick={() => startCheckout("exclusive", beat)}
              className="bg-[#D65BB4] px-4 py-2.5 text-xs uppercase tracking-widest font-bold hover:shadow-[0_0_32px_rgba(214,91,180,0.5)] transition-shadow"
            >
              Own It
            </button>
          ) : (
            <span className="flex items-center gap-1.5 px-4 py-2.5 text-xs uppercase tracking-widest text-white/40 border border-white/10">
              <Lock size={12} /> Sold
            </span>
          )}
        </div>
      </div>

      <AnimatePresence>
        {leaseOpen && isCurrent && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
            data-testid={`beat-lease-menu-${slug}`}
          >
            <div className="border-t border-white/10 px-5 md:px-8 py-5 flex flex-wrap gap-3">
              {leaseTiers.map((t) => (
                <button
                  key={t.lookup_key}
                  data-testid={`beat-lease-option-${slug}-${t.lookup_key}`}
                  onClick={() => startCheckout(t.lookup_key, beat)}
                  className="group flex-1 min-w-[180px] text-left border border-white/15 hover:border-[#D65BB4] bg-black/40 px-5 py-4 transition-colors"
                >
                  <div className="text-xs uppercase tracking-widest text-white/60 group-hover:text-white">{t.name}</div>
                  <div className="font-display font-bold text-lg mt-1 group-hover:text-[#D65BB4] transition-colors">
                    ${t.price.toFixed(2)}
                  </div>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Vault = () => {
  const audioRef = useRef(null);
  const [current, setCurrent] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [leaseOpen, setLeaseOpen] = useState(null);

  const { data: beats = [], isLoading } = useQuery({
    queryKey: ["beats"],
    queryFn: async () => normalizeBeats((await api.get("/beats")).data),
  });
  const { data: tiers = [] } = useQuery({
    queryKey: ["tiers"],
    queryFn: async () => (await api.get("/license-tiers")).data,
  });

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => {
      setTime(a.currentTime);
      if (a.currentTime >= PREVIEW_CAP) {
        a.pause();
        setPlaying(false);
      }
    };
    const onEnded = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("ended", onEnded);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("ended", onEnded);
    };
  }, []);

  const load = (beat) => {
    const a = audioRef.current;
    a.src = `${API_BASE}/audio/${beat.audio_path.split("/").pop()}`;
    a.currentTime = 0;
    setTime(0);
    setCurrent(beat.id);
    return a;
  };

  const onToggle = (beat) => {
    const a = audioRef.current;
    if (current === beat.id && playing) {
      a.pause();
      setPlaying(false);
      return;
    }
    const a2 = current === beat.id ? a : load(beat);
    a2.play()
      .then(() => setPlaying(true))
      .catch(() => toast.error("Preview didn't load — try again in a second."));
  };

  const onSeek = (beat, frac) => {
    const a = audioRef.current;
    const target = frac * PREVIEW_CAP;
    if (current !== beat.id) {
      load(beat);
      setTime(target);
    }
    a.currentTime = target;
  };

  return (
    <section id="vault" className="relative py-24 md:py-32 lg:py-40">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <Reveal>
          <SectionEyebrow num="01" label="The Vault" />
        </Reveal>
        <div className="flex flex-wrap items-end justify-between gap-6 mt-6 mb-14">
          <Reveal delay={0.05}>
            <h2 className="font-display font-black uppercase tracking-tight leading-[0.95] text-[clamp(1.6rem,5vw,3.5rem)]">
              Every beat. <span className="text-vice">Sixty seconds.</span> Your call.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <span className="text-xs uppercase tracking-[0.3em] text-white/50 border border-white/15 px-4 py-2" data-testid="vault-count-badge">
              Showing {beats.length} of {beats.length} beats
            </span>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <p className="text-white/60 max-w-xl mb-12">
            Hit play on any track for a free 60-second preview. Like what you hear? Pick a lease
            or lock it down with exclusive rights.
          </p>
        </Reveal>

        <div className="flex flex-col gap-3">
          {isLoading && <p className="text-white/40 uppercase tracking-widest text-sm">Loading the vault…</p>}
          {beats.map((beat, idx) => (
            <Reveal key={beat.id} delay={Math.min(idx * 0.03, 0.3)}>
              <BeatRow
                beat={beat}
                idx={idx}
                tiers={tiers}
                audio={audioRef}
                current={current}
                playing={playing}
                time={time}
                onToggle={onToggle}
                onSeek={onSeek}
                leaseOpen={leaseOpen === beat.id}
                onLeaseOpen={setLeaseOpen}
              />
            </Reveal>
          ))}
        </div>
      </div>
      <audio ref={audioRef} preload="none" className="hidden" />
    </section>
  );
};

export default Vault;
